import type { CSSProperties, ReactNode, Ref } from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getPanelSlugFromPath } from "./Portfolio/CaseStudy/registry";
import { ROUTES } from "../routes";
import { useAtomValue } from "jotai";
import { EMAIL } from "../constants";
import { textColorAtom } from "../state/textColor";
import { entrancePhaseAtom, isChromePhase } from "../state/entrance";
import { animationConfig } from "../config/animation";

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const HIDE_FOOTER_PATHS: string[] = [ROUTES.book];

/** Email-reveal transitions, sourced from animationConfig.footer.emailReveal.
 *  Four independent flavours so each line can be dialled separately:
 *   - TOP                  : top-line slide-up.
 *   - BOTTOM               : bottom-line slide-up, no delay (used on
 *                            hover-OUT so both lines snap back together).
 *   - BOTTOM_DELAYED       : same as BOTTOM plus `delayMs`, used on
 *                            hover-IN so the bottom line lags the top.
 *   - COPY_CONFIRM         : inner "Copy to clipboard" → "Copied" toggle
 *                            on click. Currently mirrors the top-line
 *                            shape but is its own knob so a longer /
 *                            slower confirmation feel is one config edit
 *                            away. */
const ER = animationConfig.footer.emailReveal;
const EMAIL_REVEAL_TOP = `transform ${ER.topLine.durationMs}ms ${ER.topLine.easing}`;
const EMAIL_REVEAL_BOTTOM = `transform ${ER.bottomLine.durationMs}ms ${ER.bottomLine.easing}`;
const EMAIL_REVEAL_BOTTOM_DELAYED = `${EMAIL_REVEAL_BOTTOM} ${ER.bottomLine.delayMs}ms`;
const EMAIL_REVEAL_COPY_CONFIRM = `transform ${ER.copyConfirm.durationMs}ms ${ER.copyConfirm.easing}`;

/** Each color-mode layer is itself positioned (absolute at the bottom of
 *  the visible area, not fixed: see Nav.tsx). The outer <footer> must NOT
 *  be — see Nav.tsx for the stacking-context / mix-blend caveat. */
const LAYER_CLASSES =
  "absolute bottom-[var(--bleed)] left-0 w-full z-[15] pointer-events-none " +
  "text-ui font-[400] sm:font-[300] tracking-wide " +
  "px-6 py-4 md:p-[3em] flex justify-between items-center";

/**
 * Site-wide footer showing availability status, email, and current date.
 *
 * **Desktop:** hovering reveals the email; clicking copies to clipboard.
 * **Mobile:** first tap reveals the email (mimics hover), second tap copies.
 * Falls back to a hidden `<textarea>` + `execCommand("copy")` when the
 * Clipboard API is unavailable (older Safari, some WebViews).
 *
 * Like the Nav, two stacked color-mode layers cross-fade via opacity
 * (blend-difference vs. solid `textColor`) so toggling `mix-blend-mode`
 * never produces a mid-transition flash — see Nav.tsx for the rationale.
 * State (hover/copied) lives here and is passed identically to both
 * layers so their visuals stay in lockstep through the cross-fade.
 */
export default function Footer() {
  const location = useLocation();
  const textColor = useAtomValue(textColorAtom);

  const today = new Date();
  const currentMonthShortName = MONTHS_SHORT[today.getMonth()];
  const emailBlockRef = useRef<HTMLButtonElement | null>(null);

  const [emailHovered, setEmailHovered] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Watch screen size
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    handleResize(); // run once on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Detect mobile by user agent (not by screen size)
  useEffect(() => {
    if (typeof navigator === "undefined") return;

    const ua = (navigator.userAgent || (navigator as Navigator & { vendor?: string }).vendor) ?? "";
    const mobileRegex =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    setIsMobile(mobileRegex.test(ua));
  }, []);

  // On mobile, reset state if user taps outside the email block
  useEffect(() => {
    if (!isMobile) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (!emailBlockRef.current) return;
      if (!emailBlockRef.current.contains(e.target as Node)) {
        setEmailHovered(false);
        setEmailCopied(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isMobile]);

  // When the user switches tabs/windows, cmd+tabbing doesn't move the
  // mouse so onMouseLeave never fires — the button stays focused with
  // emailHovered=true. On return, onFocus re-fires and the hover state
  // appears spontaneously. Reset state + blur the button when the tab
  // becomes hidden so returning gives a clean default view.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setEmailHovered(false);
        setEmailCopied(false);
        emailBlockRef.current?.blur();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Clipboard helper
  const copyEmailToClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(EMAIL);
        return true;
      }
    } catch {
      // ignore and try fallback if desktop
    }

    if (isMobile) {
      // Avoid textarea fallback on mobile (causes scroll jumps)
      return false;
    }

    // Desktop fallback with hidden textarea
    try {
      const textarea = document.createElement("textarea");
      textarea.value = EMAIL;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.fontSize = "16px";
      textarea.style.left = "-9999px";
      textarea.style.top = `${window.scrollY}px`;

      document.body.appendChild(textarea);

      try {
        textarea.focus({ preventScroll: true } as FocusOptions);
      } catch {
        textarea.focus();
      }

      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const handleEmailClick = async () => {
    // MOBILE: 2-tap system
    // Tap 1 = reveal (like hover)
    // Tap 2 = copy
    if (isMobile) {
      if (!emailHovered) {
        setEmailHovered(true);
        setEmailCopied(false);
        return;
      }
      // already revealed -> now copy
      const success = await copyEmailToClipboard();
      setEmailCopied(success);

      if (success) {
        window.setTimeout(() => setEmailCopied(false), 1600);
      } else {
        console.warn("Copy might not be supported on this browser.");
      }
      return;
    }

    // DESKTOP: click copies (hover handled by mouseenter)
    const success = await copyEmailToClipboard();
    setEmailHovered(true);
    setEmailCopied(success);

    if (success) {
      window.setTimeout(() => setEmailCopied(false), 1600);
    } else {
      console.warn("Copy might not be supported on this browser.");
    }
  };

  // Hide footer only on mobile for the book
  const shouldHide =
      HIDE_FOOTER_PATHS.includes(location.pathname) && (isMobile || isSmallScreen);

  // Fade the footer out while a panel (/about, /<project>) is open; fade
  // back in on the bare carousel or any other route.
  const onCaseStudy = getPanelSlugFromPath(location.pathname) !== null;
  // The book is the About page of 2025, kept; the footer's right side
  // says so there.
  const onBook = location.pathname === ROUTES.book;

  const chromeTransition = `opacity ${animationConfig.caseStudy.chrome.durationMs}ms ${animationConfig.caseStudy.chrome.easing}`;
  const blendActive = !textColor;

  // Retain the most recent non-null textColor on the solid layer so its
  // color stays put during the fade-out. See Nav.tsx for the rationale —
  // without this, `color` is yanked the instant textColor becomes null and
  // the text snaps to the inherited default before opacity finishes hiding it.
  const lastSolidColorRef = useRef<string | undefined>(undefined);
  if (textColor) lastSolidColorRef.current = textColor;

  // Bundle of every prop both layers need so they render identical content
  // through the cross-fade.
  const contentProps = {
    onBook,
    emailHovered,
    emailCopied,
    currentMonthShortName,
    year: today.getFullYear(),
    onMouseEnter: () => { if (!isMobile) setEmailHovered(true); },
    onMouseLeave: () => {
      if (!isMobile) {
        setEmailHovered(false);
        setEmailCopied(false);
      }
    },
    onFocus: () => { if (!isMobile) setEmailHovered(true); },
    onBlur: () => {
      if (!isMobile) {
        setEmailHovered(false);
        setEmailCopied(false);
      }
    },
    onClick: handleEmailClick,
  };

  // Combined opacity multiplier: cross-fade × case-study-fade. Folding both
  // onto the layers (instead of opacity on the outer footer) keeps the
  // outer footer in the root stacking context so the blend layer can see
  // the page behind it. Both fade durations are equal so a single
  // `opacity` transition covers them.
  const caseStudyMul = onCaseStudy
      ? animationConfig.caseStudy.footerFade.hiddenOpacity
      : 1;
  // Portfolio first-load entrance: the footer holds back while the loader
  // is up (its corner shows the counter) and fades in as the slides
  // settle, on the entrance's own chrome-in curve. Held back it has no
  // transition, so the leave at the first paint is instant: the phase
  // flips to "boot" right after mount, and easing that out would show the
  // footer fading on a cold load.
  const entrancePhase = useAtomValue(entrancePhaseAtom);
  const chromeIn = isChromePhase(entrancePhase);
  const entranceMul = chromeIn ? 1 : 0;
  const CI = animationConfig.portfolio.entrance.chromeIn;
  const layerTransition = !chromeIn
      ? "none"
      : entrancePhase === "settling"
        ? `opacity ${CI.durationMs}ms ${CI.easing}`
        : chromeTransition;

  return shouldHide ? null : (
      // Outer <footer> stays at default flow (no positioning, no opacity)
      // so it doesn't create a stacking context that would trap the
      // blend layer's mix-blend-mode. Both children are position:fixed
      // siblings in the root context. See Nav.tsx for the full rationale.
      <footer {...(onCaseStudy || !chromeIn ? { inert: "" } : {})}>
        <FooterLayer
            active={blendActive}
            opacityMul={caseStudyMul * entranceMul}
            transition={layerTransition}
            className={`${LAYER_CLASSES} text-white mix-blend-difference`}
        >
          <FooterContent
              {...contentProps}
              interactive={blendActive}
              buttonRef={emailBlockRef}
          />
        </FooterLayer>
        <FooterLayer
            active={!blendActive}
            opacityMul={caseStudyMul * entranceMul}
            transition={layerTransition}
            className={LAYER_CLASSES}
            style={{ color: lastSolidColorRef.current }}
        >
          <FooterContent
              {...contentProps}
              interactive={!blendActive}
          />
        </FooterLayer>
      </footer>
  );
}

/** A single color-mode layer. Only the active layer is interactive.
 *  `opacityMul` lets the parent fold an outer fade (e.g. footer hidden on
 *  case study pages) into this layer's opacity without needing a wrapper
 *  element — wrappers risk creating stacking contexts that break the
 *  blend layer's `mix-blend-mode`. */
function FooterLayer({
  active,
  opacityMul,
  transition,
  className,
  style,
  children,
}: {
  active: boolean;
  opacityMul: number;
  transition: string;
  className: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
      <div
          className={className}
          style={{ ...style, opacity: (active ? 1 : 0) * opacityMul, transition }}
          aria-hidden={!active}
          {...(active ? {} : { inert: "" })}
      >
        {children}
      </div>
  );
}

type FooterContentProps = {
  interactive: boolean;
  /** On the book page: the note that this was the About page of 2025. */
  onBook: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  emailHovered: boolean;
  emailCopied: boolean;
  currentMonthShortName: string;
  year: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onClick: () => void;
};

/** Email/availability button rendered identically in both color layers.
 *  `interactive` toggles `pointer-events` so the hidden layer doesn't
 *  intercept clicks meant for the visible one. */
function FooterContent({
  interactive,
  onBook,
  buttonRef,
  emailHovered,
  emailCopied,
  currentMonthShortName,
  year,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onClick,
}: FooterContentProps) {
  const pe = interactive ? "pointer-events-auto" : "pointer-events-none";
  // A control's name must contain its visible text (WCAG 2.5.3,
  // Lighthouse's label-content-name check), and what is visible here
  // changes: the two lines slide to "Copy to clipboard" and the email on
  // hover, and to "Copied" once clicked. So the name follows. The arrow
  // is CSS content, not text, so it needn't be spoken.
  const name = !emailHovered
    ? `Available for work ${currentMonthShortName} ${year}. Copy email ${EMAIL} to clipboard`
    : emailCopied ? `Copied ${EMAIL}` : `Copy to clipboard ${EMAIL}`;
  return (
    <>
      <button
          type="button"
          ref={buttonRef}
          className={`${pe} cursor-pointer md:select-none text-left bg-transparent border-0 p-0 m-0 font-inherit`}
          aria-label={name}
          onMouseEnter={() => { if (interactive) onMouseEnter(); }}
          onMouseLeave={() => { if (interactive) onMouseLeave(); }}
          onFocus={() => { if (interactive) onFocus(); }}
          onBlur={() => { if (interactive) onBlur(); }}
          onClick={() => { if (interactive) void onClick(); }}
          tabIndex={interactive ? 0 : -1}
      >
        {/* Top line */}
        <p className="h-7 overflow-hidden leading-8">
          <span
              className={`block ${emailHovered ? "-translate-y-1/2" : "translate-y-0"}`}
              style={{ transition: EMAIL_REVEAL_TOP }}
          >
            <span className="block">Available for work</span>

            {/* ▼▼▼ animated Copy → Copied label ▼▼▼ */}
            <span className="block text-[13px] opacity-50">
              <span className="inline-block h-8 overflow-hidden align-bottom">
                <span
                    className={`block ${emailCopied ? "-translate-y-1/2" : "translate-y-0"}`}
                    style={{ transition: EMAIL_REVEAL_COPY_CONFIRM }}
                >
                  <span className="block">Copy to clipboard</span>
                  <span className="block">Copied</span>
                </span>
              </span>
            </span>
            {/* ▲▲▲ animated Copy → Copied label ▲▲▲ */}
          </span>
        </p>

        {/* A space between the lines, so their visible words read as a
            phrase ("Available for work Oct 2026") that the name above
            contains; it renders as nothing between two blocks. */}
        {" "}
        {/* Bottom line — on hover-IN, lags the top by bottomLine.delayMs so
            the two lines don't slide as one block. On hover-OUT, both lines
            slide back together (no delay). */}
        <p className="h-7 overflow-hidden leading-8">
          <span
              className={`block ${emailHovered ? "-translate-y-1/2" : "translate-y-0"}`}
              style={{ transition: emailHovered ? EMAIL_REVEAL_BOTTOM_DELAYED : EMAIL_REVEAL_BOTTOM }}
          >
            <span className="block">{currentMonthShortName} {year} <span className="inline-block after:inline-block after:text-[0.95em] after:-translate-y-[0.1em] after:content-['↗']" /></span>
            <span className="block">{EMAIL}</span>
          </span>
        </p>
      </button>
      {/* The book's provenance, set like the availability opposite. */}
      {onBook && (
        <p className="text-right leading-8">
          <span className="block">The About page</span>
          <span className="block">2025</span>
        </p>
      )}
    </>
  );
}
